import { createSelector } from 'reselect';
import { SurveyState } from 'store/SurveyState';
import {
  selectIsEmailDistribution,
  selectIsKioskDistribution,
  selectIsWebLinkDistribution,
} from 'selector/distributionSelector';
import { isSaveAndContinueAvailableForProjectKind } from 'selector/surveySelector';

import * as metaResourceSelector from './metaResourceSelector';

// Defined in metaResourceSelector so distribution/survey selectors can read meta without an import cycle.
// Re-assigned (not re-exported) so tests can keep spying on these exports.
export const selectMetaResource = metaResourceSelector.selectMetaResource;
export const selectMeta = metaResourceSelector.selectMeta;

// TODO: Cleanup the logic related to this selector once https://buzzing.atlassian.net/browse/L1S-715 is done.
export const selectShouldReplaceImage = createSelector(
  selectMeta,
  (meta) => meta?.getProjectId() === 6806
);

export const selectError = (state: SurveyState) => state?.error;

export const selectIsMultiLanguageEnabled = createSelector(selectMeta, (meta) =>
  meta?.getMultiLanguageEnabled()
);

export const selectHasProgressBar = createSelector(selectMeta, (meta) =>
  Boolean(meta?.hasProgressBar())
);

export const selectSurveyLogoUrl = createSelector(selectMeta, (meta) => meta?.getSurveyLogoUrl());

export const selectHasAutomaticNumbering = createSelector(selectMeta, (meta) =>
  Boolean(meta?.hasAutomaticNumbering())
);

export const selectHasSaveAndContinue = createSelector(
  selectMeta,
  selectIsEmailDistribution,
  selectIsWebLinkDistribution,
  selectIsKioskDistribution,
  isSaveAndContinueAvailableForProjectKind,
  (
    meta,
    isEmailDistribution,
    isWebLinkDistribution,
    isKioskDistribution,
    isSaveAndContinueAvailableForProjectKind
  ) => {
    return (
      meta?.hasSaveAndContinue() &&
      (isEmailDistribution || isWebLinkDistribution || isKioskDistribution) &&
      isSaveAndContinueAvailableForProjectKind
    );
  }
);

export const selectAlmostFinishedMessage = createSelector(
  selectMeta,
  (meta) =>
    meta?.getAlmostFinishedContent() ||
    `You have now reached the end of the questions.
Once you press ‘Complete Survey’, your answers will be sent to WorkBuzz and you will not be able to change these.`
);

export const selectSaveAndContinueMessage = createSelector(
  selectMeta,
  (meta) =>
    meta?.getSaveAndContinueContent() ||
    `Please supply your e-mail address to save your progress. 
A unique link will be emailed to you that will allow you to continue from the last question answered. 
If you have not received the email please check your spam or junk mail folder.`
);

export const selectThankYouMessage = createSelector(
  selectMeta,
  (meta) =>
    meta?.getThankYouPageContent() ||
    'Thank you for taking the time to complete the survey. Your time and thoughts are greatly appreciated.'
);

export const selectSurveyClosedMessage = createSelector(
  selectMeta,
  (meta) =>
    meta?.getClosedPageContent() ||
    'The survey you are trying to access is closed. Please contact your HR leader if you feel this is an error.'
);
